<template>
  <page-layout>
    <template #header>
      <page-header :title="t('quotations')">
        <template #actions>
          <Link
            class="flex items-center space-x-2 group"
            :href="route('quotations.create')"
          >
            <PlusCircleIcon
              class="text-green-500 w-8 h-8 group-hover:text-green-400"
            />
            <p class="text-gray-700">
              {{ t("create") }}
            </p>
          </Link>
        </template>
      </page-header>
    </template>

    <template #content>
      <div class="mt-8">
        <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 class="text-lg leading-6 font-medium text-gray-900">
            {{ t("overview") }}
          </h2>
          <div
            class="mt-2 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
          >
            <!-- Card -->
            <div class="bg-white overflow-hidden shadow rounded-lg">
              <div class="p-5">
                <div class="flex items-center">
                  <div class="flex-shrink-0">
                    <ExclamationCircleIcon
                      class="h-6 w-6 text-light-blue-400"
                    />
                  </div>
                  <div class="ml-5 w-0 flex-1">
                    <dl>
                      <dt class="text-sm font-medium text-gray-500 truncate">
                        {{ t("open_quotations") }}
                      </dt>
                      <dd>
                        <div class="text-lg font-medium text-gray-900">
                          {{ openQuotations }}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div class="bg-white overflow-hidden shadow rounded-lg">
              <div class="p-5">
                <div class="flex items-center">
                  <div class="flex-shrink-0">
                    <XCircleIcon class="h-6 w-6 text-red-400" />
                  </div>
                  <div class="ml-5 w-0 flex-1">
                    <dl>
                      <dt class="text-sm font-medium text-gray-500 truncate">
                        {{ t("rejected_quotations") }}
                      </dt>
                      <dd>
                        <div class="text-lg font-medium text-gray-900">
                          {{ rejectedQuotations }}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div class="bg-white overflow-hidden shadow rounded-lg">
              <div class="p-5">
                <div class="flex items-center">
                  <div class="flex-shrink-0">
                    <CheckCircleIcon class="h-6 w-6 text-green-400" />
                  </div>
                  <div class="ml-5 w-0 flex-1">
                    <dl>
                      <dt class="text-sm font-medium text-gray-500 truncate">
                        {{ t("confirmed_quotations") }}
                      </dt>
                      <dd>
                        <div class="text-lg font-medium text-gray-900">
                          {{ confirmedQuotations }}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <h2
          class="
            max-w-6xl
            mx-auto
            mt-8
            px-4
            text-lg
            leading-6
            font-medium
            text-gray-900
            sm:px-6
            lg:px-8
          "
        >
          {{ t("quotations") }}
        </h2>

        <!-- Activity list (smallest breakpoint only) -->
        <div class="sm:hidden">
          <ul
            class="
              mt-2
              divide-y divide-gray-200
              overflow-hidden
              shadow
              sm:hidden
            "
          >
            <li v-for="quotation in quotations.data" :key="quotation.id">
              <Link
                :href="
                  route('quotations.show', {
                    quotation: quotation.id,
                  })
                "
                class="block px-4 py-4 bg-white hover:bg-gray-50"
              >
                <span class="flex items-center space-x-4">
                  <span class="flex-1 flex space-x-2 truncate">
                    <span class="flex flex-col text-gray-500 text-sm truncate">
                      <div class="block truncate">
                        <p
                          class="
                            text-gray-500
                            truncate
                            group-hover:text-gray-900
                          "
                        >
                          {{ quotation.id }}
                        </p>
                        <p
                          class="
                            text-gray-500
                            truncate
                            group-hover:text-gray-900
                          "
                        >
                          {{
                            quotation.customer ? quotation.customer.name : ""
                          }}
                        </p>
                      </div>

                      <p class="text-gray-500">
                        {{
                          t("concepts", {
                            number: quotation.quotationConcepts.data.length,
                          })
                        }}
                      </p>
                      <p class="text-gray-900 font-medium">
                        {{ quotation.formatted_total }}
                      </p>
                      <p
                        :class="[
                          statusStyles[quotation.status],
                          'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize',
                        ]"
                      >
                        {{ t(quotation.status) }}
                      </p>
                    </span>
                  </span>
                  <ChevronRightIcon
                    class="flex-shrink-0 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </span>
              </Link>
            </li>
          </ul>
          <pagination class="py-3 md:px-0" :pagination="quotations" />
        </div>

        <!-- Activity table (small breakpoint and up) -->
        <div class="hidden sm:block">
          <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="flex flex-col mt-2">
              <div
                class="
                  align-middle
                  min-w-full
                  overflow-x-auto
                  shadow
                  overflow-hidden
                  sm:rounded-lg
                "
              >
                <table class="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th
                        class="
                          px-6
                          py-3
                          bg-gray-50
                          text-left text-xs
                          font-medium
                          text-gray-500
                          uppercase
                          tracking-wider
                        "
                      >
                        {{ t("quotation") }}
                      </th>
                      <th
                        class="
                          px-6
                          py-3
                          bg-gray-50
                          text-right text-xs
                          font-medium
                          text-gray-500
                          uppercase
                          tracking-wider
                        "
                      >
                        {{ t("total") }}
                      </th>
                      <th
                        class="
                          px-6
                          py-3
                          bg-gray-50
                          text-right text-xs
                          font-medium
                          text-gray-500
                          uppercase
                          tracking-wider
                        "
                      >
                        {{ t("status") }}
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr
                      v-for="quotation in quotations.data"
                      :key="quotation.id"
                      class="bg-white"
                    >
                      <td
                        class="
                          max-w-0
                          w-full
                          px-6
                          py-4
                          whitespace-nowrap
                          text-sm text-gray-900
                        "
                      >
                        <div class="flex items-center">
                          <Link
                            :href="
                              route('quotations.show', {
                                quotation: quotation.id,
                              })
                            "
                            class="group inline-flex space-x-2 truncate text-sm"
                          >
                            <div class="block truncate">
                              <p
                                class="
                                  text-light-blue-500
                                  truncate
                                  group-hover:text-light-blue-900
                                "
                              >
                                {{ quotation.id }}
                              </p>
                              <p
                                class="
                                  text-gray-500
                                  truncate
                                  group-hover:text-light-blue-900
                                "
                              >
                                {{
                                  quotation.customer
                                    ? quotation.customer.name
                                    : ""
                                }}
                              </p>
                            </div>
                          </Link>
                        </div>
                      </td>
                      <td
                        class="
                          px-6
                          py-4
                          text-right
                          whitespace-nowrap
                          text-sm text-gray-500
                        "
                      >
                        <p class="text-gray-500">
                          {{
                            t("concepts", {
                              number: quotation.quotationConcepts.data.length,
                            })
                          }}
                        </p>
                        <p class="text-gray-900 font-medium">
                          {{ quotation.formatted_total }}
                        </p>
                      </td>
                      <td
                        class="
                          px-6
                          py-4
                          text-right
                          whitespace-nowrap
                          text-sm text-gray-500
                        "
                      >
                        <p
                          :class="[
                            statusStyles[quotation.status],
                            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize',
                          ]"
                        >
                          {{ t(quotation.status) }}
                        </p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <pagination class="py-3 md:px-0" :pagination="quotations" />
          </div>
        </div>
      </div>
    </template>
  </page-layout>
</template>

<script>
import PageHeader from "./../Shared/Organisms/PageHeader";
import AppLayout from "./../Layouts/AppLayout";
import Pagination from "./../Shared/Molecules/Pagination";
import PageLayout from "./../Layouts/PageLayout";
import {
  PlusCircleIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  CheckCircleIcon,
} from "@heroicons/vue/outline";
import { useI18n } from "vue-i18n";

const statusStyles = {
  opportunity: "bg-gray-100 text-gray-800",
  negotiation: "bg-light-blue-100 text-light-blue-800",
  closing: "bg-indigo-100 text-indigo-800",
  confirmed: "bg-blue-100 text-blue-800",
  rejected: "bg-red-100 text-red-800",
};

export default {
  layout: AppLayout,

  props: [
    "quotations",
    "filters",
    "openQuotations",
    "rejectedQuotations",
    "confirmedQuotations",
  ],

  components: {
    PageLayout,
    Pagination,
    PageHeader,
    AppLayout,
    PlusCircleIcon,
    ChevronRightIcon,
    ExclamationCircleIcon,
    XCircleIcon,
    CheckCircleIcon,
  },

  data() {
    return {
      form: {
        query: this.filters.search,
      },
    };
  },

  setup() {
    const { t } = useI18n();
    return { t, statusStyles };
  },

  methods: {
    searchQuotations() {
      this.$inertia.visit(this.route("quotations.index"), {
        data: {
          search: this.form.query,
        },
      });
    },
  },
};
</script>

<i18n>
{
  "es": {
    "quotations": "Cotizaciones",
    "quotation": "Cotización",
    "create": "Nueva cotización",
    "overview": "Información general",
    "customer": "Cliente",
    "total": "Total",
    "concepts": "{number} concepto(s)",
    "status": "Estatus",
    "expiration_date": "Expira: {date}",
    "opportunity": "Oportunidad",
    "negotiation": "Negociación",
    "closing": "Cerrando",
    "confirmed": "Confirmada",
    "rejected": "Rechazada",
    "open_quotations": "Cotizaciones abiertas",
    "rejected_quotations": "Cotizaciones rechazadas",
    "confirmed_quotations": "Cotizaciones confirmadas",

  }
}
</i18n>
